import React, { useEffect, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigationType,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";

// CSS imports
import "../styles/main.css";
import "react-circular-progressbar/dist/styles.css";
import "react-toastify/dist/ReactToastify.css";

// Helper imports
import { sendRouteToParent } from "src/helpers/common";

// Eager load critical components
import AuthUser from "src/components/organisms/AuthUser";
import InfiniteLoader from "../components/atoms/Loader/InfiniteLoader";
import BCAHoc from "src/components/atoms/BCAWrapper";
import SpinnerLoader from "src/components/atoms/Loader/SpinnerLoader";

// Lazy load page components
const HomePage = React.lazy(() => import("./HomePage"));
const DietPage = React.lazy(() => import("./DietPage"));
const ExercisePage = React.lazy(() => import("./ExercisePage"));
const InvalidPage = React.lazy(() => import("./InvalidPage"));
const SearchFoodPage = React.lazy(() => import("./SearchFoodPage"));
const SearchFoodResultPage = React.lazy(() => import("./SearchFoodResultPage"));
const ExerciseMoreVideoPage = React.lazy(
  () => import("./ExerciseMoreVideoPage")
);
const ProgressRecordedDataPage = React.lazy(
  () => import("./ProgressRecordedDataPage")
);
const DietInsightsPage = React.lazy(() => import("./DietInsightsPage"));
const ExerciseInsightsPage = React.lazy(() => import("./ExerciseInsightsPage"));
const AppointmentPage = React.lazy(() => import("./AppointmentPage"));
const SelectHealthCoach = React.lazy(() => import("./SelectCoachPage"));
const BookAppointmentSlotPage = React.lazy(
  () => import("./BookAppointmentSlotPage")
);
const AppointmentConfirmationScreen = React.lazy(
  () => import("./AppointmentConfirmationScreen")
);
const VitalsProgressPage = React.lazy(() => import("./ProgressPage"));
const ProgressSection = React.lazy(() => import("./ProgressSection"));
const DeviceLogScreen = React.lazy(() => import("./DeviceLogScreen"));
const DeviceErrorPage = React.lazy(() => import("./DeviceErrorPage"));
const DeviceResultPage = React.lazy(() => import("./DeviceResultPage"));
const DeviceProvideDetails = React.lazy(() => import("./DeviceProvideDetails"));
const SearchProgressDetailsPage = React.lazy(() =>
  import("./SearchProgressDetailsPage").then((module) => ({
    default: module.SearchProgressDetailsPage,
  }))
);
const VitalProgressDetailsPage = React.lazy(
  () => import("./ProgressDetailsPage")
);
const DeviceDetailsScreen = React.lazy(() => import("./DeviceDetailsScreen"));

// Loading fallback component
const PageLoadingFallback = () => (
  <div className="flex items-center justify-center h-screen bg-background">
    <SpinnerLoader size={35} />
  </div>
);

const RouteObserver = () => {
  const location = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    // Send current route to parent whenever location changes
    sendRouteToParent(navigationType);
  }, [location.pathname, navigationType]);

  return null;
};

const AppRoutes: React.FC = () => {
  const WrappedDeviceListSection = BCAHoc(ProgressSection);
  const WrappedDeviceLogScreen = BCAHoc(DeviceLogScreen);
  const WrappedDeviceProvideDetails = BCAHoc(DeviceProvideDetails);
  const WrappedDeviceErrorPage = BCAHoc(DeviceErrorPage);
  const WrappedDeviceResultPage = BCAHoc(DeviceResultPage);
  return (
    <div className="">
      <div className="relative max-w-lg mx-auto h-screen">
        <Router>
          <RouteObserver />
          <Suspense fallback={<PageLoadingFallback />}>
            <Routes>
              <Route path="/" element={<AuthUser component={HomePage} />} />
              <Route path="/diet" element={<AuthUser component={DietPage} />} />
              <Route
                path="/diet/search-food"
                element={<AuthUser component={SearchFoodPage} />}
              />
              <Route
                path="/diet/search-food-result"
                element={<AuthUser component={SearchFoodResultPage} />}
              />
              <Route
                path="/diet/insights"
                element={<AuthUser component={DietInsightsPage} />}
              />
              <Route
                path="/exercise"
                element={<AuthUser component={ExercisePage} />}
              />
              <Route
                path="/exercise/insights"
                element={<AuthUser component={ExerciseInsightsPage} />}
              />
              <Route
                path="/exercise/more-videos"
                element={<AuthUser component={ExerciseMoreVideoPage} />}
              />
              <Route
                path="/progress/vitals"
                element={<AuthUser component={VitalsProgressPage} />}
              />
              <Route
                path="/progress/vitals/progress-details"
                element={<AuthUser component={VitalProgressDetailsPage} />}
              />
              <Route
                path="/progress/vitals/progress-details/recorded-data"
                element={<AuthUser component={ProgressRecordedDataPage} />}
              />
              <Route
                path="/progress/vitals/search-patient-log"
                element={<AuthUser component={SearchProgressDetailsPage} />}
              />
              <Route
                path="/appointment/my-appointments"
                element={<AuthUser component={AppointmentPage} />}
              />
              <Route
                path="/appointment/book-coach"
                element={<AuthUser component={SelectHealthCoach} />}
              />
              <Route
                path="/appointment/book-slot"
                element={<AuthUser component={BookAppointmentSlotPage} />}
              />
              <Route
                path="/appointment/confirm-slot"
                element={<AuthUser component={AppointmentConfirmationScreen} />}
              />
              <Route
                path="/progress/progress-device-section"
                element={<AuthUser component={WrappedDeviceListSection} />}
              />
              <Route
                path="/progress/device-logs"
                element={<AuthUser component={WrappedDeviceLogScreen} />}
              />
              <Route
                path="/progress/device-result-page"
                element={<AuthUser component={WrappedDeviceResultPage} />}
              />
              <Route
                path="/progress/device-error"
                element={<AuthUser component={WrappedDeviceErrorPage} />}
              />
              <Route
                path="/progress/device-provide-details"
                element={<AuthUser component={WrappedDeviceProvideDetails} />}
              />
              <Route
                path="/progress/device-details"
                element={<AuthUser component={DeviceDetailsScreen} />}
              />

              <Route path="*" Component={InvalidPage} />
            </Routes>
          </Suspense>
        </Router>
        <InfiniteLoader />
        <ToastContainer />
      </div>
    </div>
  );
};

export default AppRoutes;
